// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    faxPattern,
    longZipCodePattern,
    phonePattern,
    shortZipCodePattern,
    TextMask,
} from "@mcmc/App/Libraries/react-text-mask";
import { Dispatch, SetStateAction } from "react";

const requiredMessage = siteMessage("McMc_Field_Required");

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const maxFileSize = 15 * 1024 * 1024; // 15MB
const validFileTypes = ["pdf"];

export type ErrorResult = boolean | typeof requiredMessage;

export type FormValidator = (value: any, options?: any) => ErrorResult;

export interface FormValidators {
    required: FormValidator;
    email: FormValidator;
    file: FormValidator;
    phone: FormValidator;
    zipCode: FormValidator;
    fax: FormValidator;
}

export const validators: FormValidators = {
    required: (value: any) => {
        if (value) {
            return false;
        }
        return siteMessage("McMc_Field_Required");
    },
    email: (value: string) => {
        if (!value || emailRegexp.test(value)) {
            return false;
        }
        return siteMessage("McMc_EmailAddress_ValidEmail");
    },
    file: (file: File) => {
        if (file) {
            const fileExtension = getFileExtension(file.name);
            if (validFileTypes.indexOf(fileExtension) === -1) {
                return siteMessage("McMc_File_InValidFileType");
            }
            if (file.size > maxFileSize) {
                return siteMessage("McMc_File_InValidFileSize");
            }
        }
        return false;
    },
    phone: (value: string) => {
        if (value && value.length !== phonePattern.length) {
            return siteMessage("McMc_Phone_InValidPhoneFormat");
        }
        return false;
    },
    zipCode: (value: string, options?: { country: string }) => {
        const country = options ? options.country : "US";
        if (
            country === "US" &&
            value &&
            value.length !== shortZipCodePattern.length &&
            value.length !== longZipCodePattern.length
        ) {
            return siteMessage("McMc_ZipCode_InValidFormat");
        }
        return false;
    },
    fax: (value: string) => {
        if (value && value.length !== faxPattern.length) {
            return siteMessage("McMc_Fax_InValidFaxFormat");
        }
        return false;
    },
};

export type ValidatorName = keyof FormValidators;

export interface FieldAttributes {
    id?: string;
    name?: string;
    type?: string;
    maxLength?: number;
    required?: boolean;
    label?: string;
    dateTimePickerProps?: any;
    min?: number;
}

export interface FormState {
    isValid: boolean;
    isSubmitted: boolean;
    fields: Dictionary<FormField<any>>;
}

export type FormField<T> = {
    value: T;
    validators?: ValidatorName[] | ((formState: FormState) => ValidatorName[]);
    error?: ErrorResult;
    mask?: TextMask;
    attrs?: FieldAttributes;
};

export class AForm<T extends FormState> {
    state: T;
    setState: Dispatch<SetStateAction<T>>;

    constructor(state: T, setState: Dispatch<SetStateAction<T>>) {
        this.state = state;
        this.setState = setState;
        this.validate();
    }

    public validate = () => {
        let result = true;
        for (const fieldKey in this.state.fields) {
            const field: FormField<any> = this.state.fields[fieldKey] as any;
            const validatorKeys =
                typeof field.validators === "function" ? field.validators(this.state) : field.validators;
            if (validatorKeys && validatorKeys.length > 0) {
                validatorKeys.every((key: ValidatorName) => {
                    field.error = validators[key](field.value);
                    result = result && !field.error;
                    return !field.error;
                });
            } else {
                field.error = false;
            }
        }

        this.state.isValid = result;
        return result;
    };

    public updateState() {
        this.setState({ ...this.state });
    }

    public onFieldChange(field: FormField<any>, newValue: any) {
        if (field.mask) {
            field.value = field.mask(newValue, field.value);
        } else {
            field.value = newValue;
        }
        this.validate();
        this.updateState();
    }

    public setSubmitted() {
        this.setState({ ...this.state, isSubmitted: true } as T);
    }

    public setPrestine() {
        this.setState({ ...this.state, isSubmitted: false } as T);
    }
}
